import React from 'react';
import PropTypes from 'prop-types';
import Container from 'components/Container';
import Localize from 'components/Localize';
import { roles } from 'utils/access-rules/constants';
import { getChooseInternshipText } from 'pages/ApplicantProfile/applicantProfileUtils';
import {
  InternshipSwitcherWrap,
  SelectStyled,
  RequestInternshipOption,
} from './InternshipSwitcherStyled';

// Graceful fallback: fehlt der i18n-Key noch (Backend-Fixture nicht deployt),
// rendert react-localize-redux statt "Missing translationId: ..." diesen Text.
// Kein leerer Eintrag, kein Crash.
const REQUEST_CTA_FALLBACK = 'Neues Praktikum';

// 🔴 Sentinel-Wert des Handlungs-Eintrags. Er ist bewusst KEINE Praktikums-pk und auch
// nicht `null`/`'no-internship'` (die tragen beide schon eine Bedeutung: "kein Praktikum
// gewaehlt"). Ein eigener Wert ist noetig, weil der Eintrag im selben Menue steht wie
// die Auswahlwerte, aber keiner ist.
export const REQUEST_INTERNSHIP_VALUE = '__request-new-internship__';

// Thomas-Entscheid 18.08. (Variante A): Der CTA "Neues Praktikum" steht nicht mehr als
// eigener Knopf neben der Auswahl, sondern als letzter Eintrag IM Menue.
// ⚠️ Der Preis ist gemessen und bewusst getragen: 544 von 1.426 Bewerbern haben gar kein
// Praktikum; fuer sie enthaelt das Menue nur "Kein Praktikum" und diesen Eintrag.
const InternshipSwitcher = ({
  options,
  onChange,
  selectedInternshipId,
  userRole,
  onRequestNewInternship,
}) => {
  const showRequestOption = userRole === roles.Applicant && !!onRequestNewInternship;
  const requestOption = {
    value: REQUEST_INTERNSHIP_VALUE,
    label: (
      <Localize
        id="request-new-internship-cta"
        options={{ onMissingTranslation: () => REQUEST_CTA_FALLBACK }}
      />
    ),
    isRequestAction: true,
  };
  const allOptions = showRequestOption ? [...options, requestOption] : options;

  // 🔴 Der Handlungs-Eintrag darf NIE als Auswahl durchgereicht werden: `onChange` fuehrt
  // im Container eine Navigation auf `/<pfad>/<value>` aus — mit dem Sentinel entstuende
  // eine Route auf ein Praktikum, das es nicht gibt. Deshalb wird hier abgefangen und
  // NICHT weitergereicht.
  const handleChange = (selected) => {
    if (selected && selected.value === REQUEST_INTERNSHIP_VALUE) {
      onRequestNewInternship();
      return;
    }
    onChange(selected);
  };

  // ⛔ Der Sentinel darf auch nie als aktueller Wert erscheinen — sonst stuende die
  // Handlung im geschlossenen Feld, als waere sie das gewaehlte Praktikum.
  const currentValue = options.find(({ value }) => value === selectedInternshipId);

  return (
    <Container>
      <InternshipSwitcherWrap className="internship-switcher">
        {userRole === roles.Applicant ? (
          <Localize id="internship-you-are-currently-work" />
        ) : (
          <Localize id={getChooseInternshipText(userRole)} />
        )}
        <SelectStyled
          options={allOptions}
          onChange={handleChange}
          value={currentValue}
          defaultValue={options.length > 1 ? options[1] : options[0]}
          formatOptionLabel={(option) => (option.isRequestAction
            ? (
              <RequestInternshipOption className="request-new-internship-cta">
                {option.label}
              </RequestInternshipOption>
            )
            : option.label)}
        />
      </InternshipSwitcherWrap>
    </Container>
  );
};

InternshipSwitcher.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  selectedInternshipId: PropTypes.number,
  userRole: PropTypes.string,
  onRequestNewInternship: PropTypes.func,
};

InternshipSwitcher.defaultProps = {
  selectedInternshipId: null,
  userRole: null,
  onRequestNewInternship: null,
};

export default InternshipSwitcher;
